import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "../slugs/vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const vanWithChauffeur: ChauffeurPage = {
  slug: "van-with-chauffeur",
  title: "Van with chauffeur — 7-seat car service",
  subtitle:
    "Mercedes V-Class with chauffeur: the space for family, small groups and luggage across the French Riviera.",
  keywords: [
    "van with chauffeur Cannes",
    "7 seat chauffeur service",
    "people carrier with chauffeur Nice airport",
    "family minivan chauffeur French Riviera",
  ],
  hero: {
    title: "Van with chauffeur on the French Riviera — 7-seat car service",
    subtitle:
      "Mercedes V-Class: up to 7 passengers and their luggage in one comfortable vehicle. Ideal for family, golf, ski and airport transfers.",
    keywords: ["van with chauffeur", "7-seat car service", "V-Class"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes V-Class (van) with chauffeur in Cannes",
    badges: [
      "Up to 7 passengers",
      "Large luggage space",
      "Dedicated chauffeur",
    ],
  },
  benefits: {
    heading: "Why the van with chauffeur from KS Rent Services",
    items: [
      {
        title: "7 seats + luggage",
        description:
          "Up to 7 passengers and a large luggage volume: everyone and all the gear in a single vehicle.",
      },
      {
        title: "Premium van comfort",
        description:
          "Generous space, individual seats and easy access: the comfort of a high-end people carrier, not a standard shuttle.",
      },
      {
        title: "Family, golf & ski",
        description:
          "Golf bags, ski equipment, strollers and suitcases: the van absorbs group departures without compromise.",
      },
      {
        title: "A single vehicle",
        description:
          "No group split across two sedans: you travel together, with one chauffeur and one point of contact.",
      },
    ],
  },
  services: {
    heading: "Van with chauffeur services",
    services: [
      {
        title: "Family airport transfers",
        text: "Nice airport and stations: family, friends and all the luggage in a single trip.",
      },
      {
        title: "Golf & ski",
        text: "Golf departures across the French Riviera and links to the resorts, with room for all the equipment.",
      },
      {
        title: "Group outings",
        text: "Excursions, dinners and events together: a comfortable van and a chauffeur at your disposal.",
      },
      {
        title: "Hourly hire",
        text: "Van and chauffeur by the hour or day for your group programmes.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Space and comfort, to travel together",
    subheading: "The right vehicle between the sedan and the VIP minibus.",
    paragraphs: [
      "The Mercedes V-Class brings together up to 7 people and their luggage in a comfortable van, ideal for families, friends and small groups.",
      "More spacious than a sedan, more nimble than a minibus: it is the right vehicle for your airport transfers and group outings.",
      "A single point of contact, transparent packages and the flexibility to adjust your programme at the last minute.",
    ],
    bullets: [
      "Van for up to 7 passengers",
      "Transparent airport packages",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How to book your van with chauffeur",
    steps: [
      "Tell us your route, your schedule and the number of passengers.",
      "Receive a clear package or a per-kilometre estimate.",
      "Assignment of the V-Class and your dedicated chauffeur.",
      "Grouped, punctual and comfortable pick-up.",
    ],
  },
  faq: [
    {
      q: "How many passengers does the van carry?",
      a: "The Mercedes V-Class carries up to 7 passengers with a large luggage volume, ideal for families and small groups.",
    },
    {
      q: "What is the difference with the VIP minibus?",
      a: "The V-Class van suits up to 7 people; beyond that, the Sprinter VIP minibus carries up to 16 passengers for larger groups.",
    },
    {
      q: "Is it suited to golf and ski?",
      a: "Yes: its load volume takes golf bags, ski equipment and suitcases without difficulty.",
    },
    vehiclePriceFaq("en", "CLASS_V", "a van with chauffeur"),
    {
      q: "How do I book?",
      a: "Tell us your route, your schedule and the number of passengers: we quickly confirm the van and your dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "VIP minibus (up to 16 seats)",
      href: "/en/chauffeur/vip-minibus",
    },
    {
      label: "Sedan with chauffeur (E-Class)",
      href: "/en/chauffeur/sedan-with-chauffeur",
    },
    { label: "All our chauffeur services", href: "/en/chauffeur" },
    {
      label: "Cannes wedding, car and chauffeur",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "A family or group transfer?",
    text: "Tell us your route and party size — fast quote for your van with chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Request a quote",
  },
  seo: {
    ...vehicleSeo(
      "en",
      "CLASS_V",
      "Van with chauffeur 7 seats (V-Class)",
      "Mercedes V-Class 7-seat van with chauffeur",
    ),
    canonical: "/en/chauffeur/van-with-chauffeur",
  },
};

export default vanWithChauffeur;
